/**
 * App
 * Корневой компонент приложения
 * Здесь собираются providers, router и глобальная конфигурация
 */

import '../index.css';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { applyPageMeta } from '@/shared/pageMeta';
// J2 (audit): the anonymous LandingPage is the LCP-critical route, so it stays eager
// (in the initial chunk, no Suspense flash). The heavy authed EditorPage (pulls TipTap),
// AuthPage and LegalPage are code-split so anonymous visitors don't download them.
import { LandingPage } from '@/pages/LandingPage';
const EditorPage = lazy(() => import('@/pages/EditorPage').then((m) => ({ default: m.EditorPage })));
const AuthPage = lazy(() => import('@/pages/AuthPage').then((m) => ({ default: m.AuthPage })));
const LegalPage = lazy(() => import('@/pages/LegalPage').then((m) => ({ default: m.LegalPage })));
const PricingPage = lazy(() => import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const ModelsPage = lazy(() => import('@/pages/ModelsPage').then((m) => ({ default: m.ModelsPage })));
const BuyInkPage = lazy(() => import('@/pages/BuyInkPage').then((m) => ({ default: m.BuyInkPage })));
const InkHistoryPage = lazy(() => import('@/pages/InkHistoryPage').then((m) => ({ default: m.InkHistoryPage })));
const DeleteAccountPage = lazy(() => import('@/pages/DeleteAccountPage').then((m) => ({ default: m.DeleteAccountPage })));
const PaymentReturnPage = lazy(() => import('@/pages/PaymentReturnPage').then((m) => ({ default: m.PaymentReturnPage })));
const ReceiptQueuePage = lazy(() => import('@/pages/ReceiptQueuePage').then((m) => ({ default: m.ReceiptQueuePage })));
import { AuthProvider } from '@/auth/AuthProvider';
import { BooksProvider } from '@/books/BooksProvider';
import { ChaptersProvider } from '@/chapters/ChaptersProvider';
import { CharactersProvider } from '@/characters/CharactersProvider';
import { GalleryProvider } from '@/gallery/GalleryProvider';
import { CoverProvider } from '@/cover/CoverProvider';
import { InkProvider } from '@/ink';
import { useAuth } from '@/auth/useAuth';
import { useBooks } from '@/books/useBooks';
import { UnsavedChangesProvider } from '@/shared/unsavedChanges';

// What an ANONYMOUS visitor sees on a given route. Authenticated users always fall through to the
// app shell regardless of `anon` — these branches only gate the `!user` case.
type AnonView = { kind: 'landing' } | { kind: 'auth'; mode?: 'login' | 'register' };

// Suspense fallback while a code-split page chunk (EditorPage/AuthPage/LegalPage) loads.
function AppLoadingFallback() {
  return (
    <main className="w-full h-screen flex items-center justify-center bg-[color:var(--color-bg-primary,#262624)]">
      <h1 className="sr-only">PairPen</h1>
      <div className="flex flex-col items-center gap-4" role="status">
        <div className="w-12 h-12 border-4 border-[color:var(--color-border-primary,#374151)] border-t-[color:var(--color-text-primary,#ffffff)] rounded-full animate-spin" aria-hidden="true" />
        <p className="text-gray-400 text-sm">Загрузка...</p>
      </div>
    </main>
  );
}

function AppContent({ anon = { kind: 'auth' } }: { anon?: AnonView } = {}) {
  const { user, profile, loading, profileLoading, profileError } = useAuth();
  const { books, booksLoading } = useBooks();
  // Public pages set their own title/description (usePageMeta); restore the
  // defaults once the user is in the app so «Вход — PairPen» doesn't stick.
  useEffect(() => {
    if (user) {
      applyPageMeta();
    }
  }, [user]);

  // Sentry (tracing + replay-on-error) initialises only once a real session exists, off the
  // anonymous landing's critical path (audit follow-up). Deferred to idle so it never competes
  // with the app-shell first paint. Anonymous-landing errors are still captured on demand via
  // the error boundary (reportError.ts → captureBoundaryError).
  useEffect(() => {
    if (!user) return;
    const start = () => void import('@/lib/sentry').then(({ initSentry }) => initSentry());
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (ric) {
      ric(start);
    } else {
      window.setTimeout(start, 2000);
    }
  }, [user]);
  const shouldShowInitialDataLoading = (profileLoading && !profile) || (booksLoading && books.length === 0);

  // Показываем загрузку пока проверяем авторизацию
  if (loading) {
    return (
      <main className="w-full h-screen flex items-center justify-center bg-[color:var(--color-bg-primary,#262624)]">
        <h1 className="sr-only">PairPen</h1>
        <div className="flex flex-col items-center gap-4" role="status">
          <div className="w-12 h-12 border-4 border-[color:var(--color-border-primary,#374151)] border-t-[color:var(--color-text-primary,#ffffff)] rounded-full animate-spin" aria-hidden="true" />
          <p className="text-gray-400 text-sm">Загрузка...</p>
        </div>
      </main>
    );
  }

  // Если не авторизован: на «/» — публичный лендинг, на /login|/signup и deep-link'ах — форма
  // авторизации (B5-9: !user → AuthPage по сохранённому URL, без /login-редиректа).
  if (!user) {
    return anon.kind === 'landing' ? <LandingPage /> : <AuthPage initialMode={anon.mode} />;
  }

  // Показываем загрузку профиля и книг
  if (shouldShowInitialDataLoading) {
    return (
      <main className="w-full h-screen flex items-center justify-center bg-[color:var(--color-bg-primary,#262624)]">
        <h1 className="sr-only">PairPen</h1>
        <div className="flex flex-col items-center gap-4" role="status">
          <div className="w-12 h-12 border-4 border-[color:var(--color-border-primary,#374151)] border-t-[color:var(--color-text-primary,#ffffff)] rounded-full animate-spin" aria-hidden="true" />
          <p className="text-gray-400 text-sm">
            {profileLoading ? 'Загрузка профиля...' : 'Загрузка книг...'}
          </p>
        </div>
      </main>
    );
  }

  // Показываем ошибку загрузки профиля
  if (profileError) {
    return (
      <main className="w-full h-screen flex items-center justify-center bg-[color:var(--color-bg-primary,#262624)]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center" role="alert">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white mb-2">Ошибка загрузки профиля</h1>
            <p className="text-gray-400 text-sm">{profileError}</p>
            {profileError.includes('Сессия устарела') && (
              <p className="text-gray-500 text-xs mt-2">Перенаправление на страницу входа...</p>
            )}
          </div>
        </div>
      </main>
    );
  }

  // Если авторизован и профиль загружен - показываем приложение
  return <EditorPage />;
}

export function App() {
  // B5-10: real per-chapter URL routes layered over the existing app shell.
  //   /book/:bookId/chapter/:chapterId[/:tab]  → deep-link / refresh restore (EditorPage
  //   reads the params and syncs book + chapter + tab). All routes render the same
  //   AppContent, so auth gating stays inline (B5-9 decision: !user → <AuthPage/> at the
  //   retained URL, no /login redirect — the intended URL is preserved by construction).
  // B7-2: anonymous public surface split off `/`:
  //   `/`        → anon sees the public LandingPage (authed → app shell, unchanged).
  //   `/signup`  → AuthPage in register mode (landing CTA "Начать бесплатно").
  //   `/login`   → AuthPage in login mode (also where the e2e auth setup logs in).
  //   `/book/...` and the `/*` fallback keep the inline auth guard (anon → AuthPage login),
  //   so deep-link/refresh restore and the `?e2eParity=1` parity harness (authed → app at `/`)
  //   are untouched.
  return (
    <BrowserRouter>
      {/* Выше всех экранов: спрашивать о несохранённой рукописи приходится и в шапке (меню книг,
          ссылки на покупку и историю, выход из профиля), и на самих страницах. */}
      <UnsavedChangesProvider>
      <AuthProvider>
        <InkProvider>
          <BooksProvider>
            <ChaptersProvider>
              <CharactersProvider>
                <GalleryProvider>
                  <CoverProvider>
                    <Suspense fallback={<AppLoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<AppContent anon={{ kind: 'landing' }} />} />
                      {/* B7-6: public legal pages — shown to everyone (anon + authed), not auth-gated. */}
                      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                      <Route path="/terms" element={<LegalPage doc="terms" />} />
                      {/* B-PAY: prices, offer and requisites must be public — ЮKassa checks all
                          three before enabling payments, and /contacts is the URL the operator
                          submits in their application. */}
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/offer" element={<LegalPage doc="offer" />} />
                      <Route path="/contacts" element={<LegalPage doc="contacts" />} />
                      {/* The privacy policy (2.2) and the registration form both point here, so the
                          page has to exist before either is published — a consent that links to a
                          404 is not an informed consent. */}
                      <Route path="/consent" element={<LegalPage doc="consent" />} />
                      {/* Privacy Policy 5.3 and the cross-border consent both point here: the
                          named recipients of a prompt and the states they sit in. */}
                      <Route path="/models" element={<ModelsPage />} />
                      {/* The purchase path /pricing has been promising («выбираете пакет в
                          приложении») and the spend history п. 4.4 of the offer names as proof
                          that a service was rendered. Both gate themselves on auth inline. */}
                      <Route path="/buy" element={<BuyInkPage />} />
                      <Route path="/history" element={<InkHistoryPage />} />
                      <Route path="/account/delete" element={<DeleteAccountPage />} />
                      {/* Куда ЮKassa возвращает человека после оплаты (return_url). */}
                      <Route path="/buy/return" element={<PaymentReturnPage />} />
                      {/* Очередь чеков оператора. Права проверяет база (is_admin + политики RLS),
                          адрес ничего не открывает сам по себе; ссылки в интерфейсе нет намеренно. */}
                      <Route path="/admin/receipts" element={<ReceiptQueuePage />} />
                      <Route path="/login" element={<AppContent anon={{ kind: 'auth', mode: 'login' }} />} />
                      <Route path="/signup" element={<AppContent anon={{ kind: 'auth', mode: 'register' }} />} />
                      <Route path="/book/:bookId/chapter/:chapterId/:tab" element={<AppContent />} />
                      <Route path="/book/:bookId/chapter/:chapterId" element={<AppContent />} />
                      <Route path="/*" element={<AppContent />} />
                    </Routes>
                    </Suspense>
                  </CoverProvider>
                </GalleryProvider>
              </CharactersProvider>
            </ChaptersProvider>
          </BooksProvider>
        </InkProvider>
      </AuthProvider>
      </UnsavedChangesProvider>
    </BrowserRouter>
  );
}


